// Angular
import { Injectable, InjectionToken, inject } from '@angular/core';

// Third party
import { select, Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';

// Local library
import { IPusher, TError } from '@amwager/util-ts';

// Local file
import * as CommonActions from './common.actions';
import * as CommonSelectors from './common.selectors';
import { ENABLE_DEV_TOOLS_REDUX } from '../../tokens';
import { ICommonState } from './common.models';



@Injectable()
export class CommonFacade {
  private readonly store: Store<ICommonState> = inject(Store);
  private readonly enableDevToolsRedux: boolean = inject(
    ENABLE_DEV_TOOLS_REDUX as InjectionToken<boolean>
  );

  /**
   * @description
   * Combine pieces of state using createSelector, and expose them as observables through the facade.
   *
   * Selectors
   *
   */
  isLoaded$(): Observable<boolean | undefined> {
    return this.store.pipe(select(CommonSelectors.getLoaded));
  }
  error$(): Observable<TError | undefined> {
    return this.store.pipe(select(CommonSelectors.getError));
  }

  isComponentView$(): Observable<boolean | undefined> {
    return this.store.select(CommonSelectors.getIsComponentView);
  }

  isShowNavigation$(): Observable<boolean> {
    return this.store.select(CommonSelectors.getIsShowNavigation);
  }

  isShowFooter$(): Observable<boolean> {
    return this.store.select(CommonSelectors.getIsShowFooter);
  }

  isAuthenticated$(): Observable<boolean> {
    return this.store.select(CommonSelectors.getIsAuthenticated);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPusherData$(): Observable<any> {
    return this.store.pipe(select(CommonSelectors.getPusherData));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isErrorAPI$(): Observable<any> {
    return this.store.pipe(select(CommonSelectors.getErrorAPI));
  }

  tmsId$(): Observable<string | undefined> {
    return this.store.select(CommonSelectors.getTmsId)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  qaTesting$(): Observable<any> {
    return this.store.pipe(select(CommonSelectors.getQaTesting));
  }

  isDismissModal$(): Observable<boolean> {
    return this.store.pipe(select(CommonSelectors.getIsDismissModal));
  }

  getBackNavigation$(): Observable<string> {
    return this.store.pipe(select(CommonSelectors.getBackNavigation));
  }

  getIsConnectedInternet$(): Observable< {
    isConnected: boolean,
    sourceInitiator: string
  } | undefined> {
    return this.store.pipe(select(CommonSelectors.getIsConnectedInternet));
  }

  /**
   * @description
   * Use the initialization action to perform one or more tasks in your Effects.
   *
   * Dispatches: Requests only, from actions
   *
   */

  /**
   * @param sourceInitiator The source component where the method was first invoked.
   *
   * @usageNotes
   *  important: don't initialized multiple times
   *
   * @example
   *  this.commonFacade.initState('LoginComponent');
   */
  initState(sourceInitiator: string): void {
    this.isLoaded$()
      .pipe(take(1))
      .subscribe(isLoaded => {
        isLoaded
          ? null
          : this.store.dispatch(CommonActions.initState({ sourceInitiator }))
      });
  }
  clearState(sourceInitiator: string): void {
    this.store.dispatch(CommonActions.clearState({ sourceInitiator }));
  }

  setIsComponentView(isComponentView: boolean): void {
    this.store.dispatch(CommonActions.setIsComponentView({ isComponentView }));
  }

  // setReProfile(): void {
  //   this.store.dispatch(CommonActions.reProfile());
  // };

  setTmsId(tmsId: string): void {
    this.store.dispatch(CommonActions.setTmsId({tmsId}));
  };

  setIsShowNavigation(isShowNavigation: boolean): void {
    this.store.dispatch(CommonActions.setIsShowNavigation({ isShowNavigation }));
  }

  setIsShowFooter(isShowFooter: boolean): void {
    this.store.dispatch(CommonActions.setIsShowFooter({ isShowFooter }));
  }

  setIsAuthenticated(isAuthenticated: boolean, sourceInitiator: string): void {
    this.store.dispatch(CommonActions.setIsAuthenticated({ isAuthenticated, sourceInitiator }));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setPusherData(command: string, data: IPusher, sourceInitiator: string): void {
    this.store.dispatch(CommonActions.pusherData({ command, data, sourceInitiator }));
  }

  setErrorAPI(isErrorAPI: {
    from?: boolean,
    message: string,
    statusCode: number,
    hasError: boolean
  }): void {
    this.store.dispatch(CommonActions.setErrorAPI({ isErrorAPI }));
  }

  setQaTesting(qaTesting: {
    showStatusCodeInToastMessage?: boolean,
    showMessageCodeInToastMessage?: boolean,
    showMessageInToastMessage?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    wagerCustom?: any
  }): void {
    this.store.dispatch(CommonActions.qaTesting({ qaTesting }));
  }

  setToDismissModal(isDismiss: boolean): void {
    this.store.dispatch(CommonActions.setToDismissModal({ isDismiss }));
  }

  setBackNavigation(sourceInitiator: string): void {
    this.store.dispatch(CommonActions.backNavigation({ sourceInitiator }));
  }

  setIsConnectedInternet(sourceInitiator: string, isConnected: boolean): void {
    this.store.dispatch(CommonActions.isConnectedInternet({ sourceInitiator, isConnected }));
  }

  // logs
  log(message: string): void {
    this.store.dispatch(CommonActions.log({ message }));
  }
  logSuccess(message: string): void {
    this.store.dispatch(CommonActions.logSuccess({ message }));
  }
  logError(message: TError, typeError?: string): void {
    this.store.dispatch(CommonActions.logError({ message, typeError }));
  }
  logModule(moduleName: string): void {
    if (this.enableDevToolsRedux) this.store.dispatch(CommonActions.logModule({ moduleName }));
  }
  logComponent(componentName: string): void {
    if (this.enableDevToolsRedux) this.store.dispatch(CommonActions.logComponent({ componentName }));
  }

}
